import type { Auth0ContextInterface } from '@auth0/auth0-react';
import { isReauthRequired } from './reauth-required';

export type RedirectGuard = { inFlight: boolean };

// Module-level singleton used by useAccessToken. Tests pass their own guard
// to wrapAccessTokenAccessor so each test starts in a clean state.
export const defaultRedirectGuard: RedirectGuard = { inFlight: false };

type Accessor = Auth0ContextInterface['getAccessTokenSilently'];
type LoginWithRedirect = Auth0ContextInterface['loginWithRedirect'];

export function wrapAccessTokenAccessor(
  raw: Accessor,
  loginWithRedirect: LoginWithRedirect,
  guard: RedirectGuard = defaultRedirectGuard
): Accessor {
  const redirectAndSuspend = (): Promise<never> => {
    if (!guard.inFlight) {
      guard.inFlight = true;
      void loginWithRedirect({
        appState: {
          returnTo: window.location.pathname + window.location.search,
        },
      });
    }

    // The browser is navigating away. Suspend the caller forever so neither
    // TanStack Query (no error to retry) nor ReconnectingWebSocket
    // (no URL to retry against) can re-enter this path.
    return new Promise<never>(() => {});
  };

  // The cast is required because Accessor is an overloaded function type in
  // @auth0/auth0-react and the overload signatures don't compose cleanly with
  // a generic async wrapper. The runtime behavior is a transparent passthrough.
  return (async (...args: Parameters<Accessor>) => {
    try {
      // biome-ignore lint/suspicious/noExplicitAny: overloaded function type doesn't compose with generic wrapper
      const token = await (raw as any)(...args);

      // auth0-spa-js resolves undefined rather than throwing when the session
      // ceiling is reached: it has already cleared the local session, so only
      // an interactive login recovers. Same treatment as login_required —
      // never hand a caller an undefined token to put in a header or a URL.
      return token ?? (await redirectAndSuspend());
    } catch (err) {
      if (!isReauthRequired(err)) {
        throw err;
      }

      return redirectAndSuspend();
    }
  }) as Accessor;
}
